import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import moment from 'moment';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import RefundLayout from '../../components/Layout';
import { Text, Title } from '../../components/Typo';
import Countdown from '../../components/Countdown';
import { StyledH4 } from 'app/pages/NewAccount/layouts';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { formatNumber } from 'utils/helper';
import useCountDown from 'utils/hooks/useCountDown';
import * as _ from 'lodash';
import {
  approveOrderRefundTicketsApi,
  cancelOrderRefundTicketsApi,
  getOrderReturnTicketsApi,
} from 'app/services/checkTicketApi';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { REFUND_TICKETS_ALLOCATED_DEBT, REFUND_TICKETS_SELECTED, SESSION_MESSAGE } from 'utils/constants';
import { ACCOUNT_MNG_MENU } from 'app/pages/NewAccount/layouts/AccountManager';
import CancellationList from './components/CancellationList';
import GiftItems from './components/GiftItems';
import { ORDER_REFUND_STATUS } from '../TicketRefundDetail';
import useWindowSize from 'app/hooks/useWindowSize';

const TicketRefundInfo = () => {
  const translations = useSelector(selectTranslations);
  const { state } = useLocation<any>();
  const history = useHistory();
  const { idHash } = useParams<any>();
  const [isLoading, setIsLoading] = useState(false);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const { device } = useWindowSize();
  const isMobile = device === 'mobile';
  const { executeRecaptcha } = useGoogleReCaptcha();
  const infoUser = useSelector(selectInfoUser);

  const [orderRefundData, setOrderRefundData] = useState<any>(
    state?.refundTicketOrderData,
  );

  const isSentCancellationRequest = useRef(false);

  const cancellationListData = orderRefundData?.tickets;

  const giftItems = orderRefundData?.gift_tickets;

  const timeDistance = useMemo(() => {
    return (
      moment(
        orderRefundData?.order?.expiredAt?.datetimeDMYHIS,
        'HH:mm:ss DD/MM/YYYY',
      ).valueOf() -
      moment(
        orderRefundData?.order?.startTime?.datetimeDMYHIS,
        'HH:mm:ss DD/MM/YYYY',
      ).valueOf()
    );
  }, [
    orderRefundData?.order?.expiredAt?.datetimeDMYHIS,
    orderRefundData?.order?.startTime?.datetimeDMYHIS,
  ]);

  const { timeRemaining, resetCountDown } = useCountDown({
    time: timeDistance,
  });

  useEffect(() => {
    resetCountDown(timeDistance < 0 ? 0 : timeDistance);
  }, [resetCountDown, timeDistance]);

  const onApproveOrderRefundTicket = async () => {
    const formData = new FormData();
    formData.append('idHash', idHash);
    try {
      if (isLoading || !orderRefundData) return;
      setIsLoading(true);
      const res = await approveOrderRefundTicketsApi(formData);
      const resData = res.data;
      history.push('/ticket-refund/successfully', {
        orderData: resData,
        originPage: state?.prevPage
          ? { prevPage: state.prevPage, targetTab: state?.targetTab }
          : '/check-tickets',
      });
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  const onCancelOrder = useCallback(
    async ({
      onBeforeGetCaptcha,
      onBefore,
      onSuccess,
      onError,
      onFinally,
    }: {
      onBeforeGetCaptcha?: () => boolean;
      onBefore?: () => void;
      onSuccess?: (data) => void;
      onError?: (error) => void;
      onFinally?: () => void;
    }) => {
      if (!executeRecaptcha || (onBeforeGetCaptcha && !onBeforeGetCaptcha()))
        return;
      const tokenCaptcha = await executeRecaptcha('cancelRefundTicketOrder');
      const formData = new FormData();
      formData.append('idHash', idHash);
      formData.append('captcha', tokenCaptcha);
      try {
        onBefore?.();
        const res = await cancelOrderRefundTicketsApi(formData);
        onSuccess?.(res.data);
      } catch (err: any) {
        onError?.(err);
      } finally {
        onFinally?.();
      }
    },
    [executeRecaptcha, idHash],
  );

  const onBack = () => {
    const handleCancelOrder = () => {
      if (isLoading) return;
      const idsSelected = orderRefundData?.tickets
        ?.map(ticket => ticket?.groupTickets)
        ?.flat(Infinity);
      onCancelOrder({
        onBeforeGetCaptcha: () => {
          setIsLoading(true);
          return !isLoading;
        },
        onSuccess: () => {
          if (state?.prevPage) {
            sessionStorage.setItem(
              SESSION_MESSAGE,
              JSON.stringify({
                message:
                  translations?.['b2b.msg.cancelOrderSuccess']?.replace(
                    '{orderNumber}',
                    orderRefundData?.order?.id,
                  ) ?? '',
                type: 'SUCCESS',
              }),
            );
            history.push(state.prevPage, {
              targetTab: 'tab-3',
            });
            return;
          }
          sessionStorage.setItem(
            REFUND_TICKETS_SELECTED,
            idsSelected.join(','),
          );

          const bpAllocatedDebt =
            orderRefundData?.tickets?.[0]?.businessPartnerAllocatedDebt?.id;

          if (!!bpAllocatedDebt)
            sessionStorage.setItem(
              REFUND_TICKETS_ALLOCATED_DEBT,
              bpAllocatedDebt,
            );

          history.push('/ticket-refund');
        },
        onFinally: () => {
          setIsLoading(false);
        },
      });
    };

    onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: '',
      content: translations?.['b2b.txt.cfRemoveRefundTicket'] ?? '',
      buttons: [
        {
          text: translations?.['b2c.txt.back'] ?? '',
          bg: '#ffff',
          border: 'solid 1px #808080',
          color: '#808080',
          onClick: () => {
            onCloseDialogNoti();
          },
        },
        {
          text: translations?.['b2b.btn.confirm'] ?? '',
          bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
          color: 'white',
          onClick: () => {
            onCloseDialogNoti();
            handleCancelOrder();
          },
        },
      ],
      closable: false,
    });
  };

  useEffect(() => {
    const loadingOrderRefundData = async () => {
      if (orderRefundData || isLoading) return;
      try {
        setIsLoading(true);
        const res = await getOrderReturnTicketsApi(idHash);
        if (
          res.data?.order?.orderState?.value === ORDER_REFUND_STATUS.INIT_STATUS
        ) {
          setOrderRefundData(res.data);
        } else {
          throw new Error('Invalid order status');
        }
      } catch (err: any) {
        history.push('/page-not-found');
      } finally {
        setIsLoading(false);
      }
    };

    loadingOrderRefundData();
  }, [history, idHash, isLoading, orderRefundData]);

  useEffect(() => {
    // Remove order refund data on history stack to ensure while reload page,
    // it will get newest data
    const clearOrderRefundDataOnHistoryStack = () => {
      if (!state?.refundTicketOrderData) return;
      const { pathname } = history.location;
      history.push(pathname);
    };

    clearOrderRefundDataOnHistoryStack();
  }, [history, state?.refundTicketOrderData]);

  useEffect(() => {
    const onTimeout = () => {
      if (
        timeRemaining > 0 ||
        !orderRefundData ||
        isNaN(timeRemaining) ||
        isSentCancellationRequest.current
      )
        return;
      onCancelOrder({
        onBeforeGetCaptcha: () => {
          setIsLoading(true);
          return !isLoading;
        },
        onSuccess: () => {
          isSentCancellationRequest.current = true;
          onOpenDialogNoti({
            type: 'ERROR',
            isShow: true,
            title: '',
            content:
              translations?.['b2b.txt.refundOrderExpried']?.replace(
                '{orderNumber}',
                orderRefundData?.order?.id,
              ) ?? '',
            buttons: [],
            closable: true,
            maskClosable: false,
            onCloseDialog: () => {
              onCloseDialogNoti();
              history.push('/account', {
                targetTab: 'tab-3',
              });
            },
          });
        },
        onFinally: () => {
          setIsLoading(false);
        },
      });
    };

    onTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    history,
    // isLoading,
    onCancelOrder,
    onCloseDialogNoti,
    onOpenDialogNoti,
    orderRefundData,
    timeRemaining,
    translations,
  ]);

  return (
    <LoadingContainer loading={!orderRefundData || isLoading}>
      {orderRefundData ? (
        <RefundLayout
          title={
            <div
              className={`d-flex justify-content-between ${
                isMobile ? 'flex-column' : 'flex-row'
              } ${isMobile ? 'align-items-start' : 'align-items-center'}`}
              style={{ width: '100%' }}
            >
              <StyledH4>
                {translations?.['b2b.txt.refundInformationAccordingPolicy'] ??
                  ''}
              </StyledH4>
              <div style={{ flexShrink: 0, margin: isMobile ? '15px 0' : 0 }}>
                {_.isInteger(timeRemaining) && (
                  <Countdown time={timeRemaining} />
                )}
              </div>
            </div>
          }
          useActionBlock
          onNext={onApproveOrderRefundTicket}
          onBack={onBack}
          menuActive={
            state?.prevPage
              ? ACCOUNT_MNG_MENU.PURCHASE_HISTORY
              : ACCOUNT_MNG_MENU.CHECK_TICKET
          }
          disabledNextBtn={infoUser?.id !== orderRefundData?.order?.agent?.id}
          disabledBackBtn={infoUser?.id !== orderRefundData?.order?.agent?.id}
        >
          <div>
            <div
              className="d-flex align-content-center"
              style={{ marginTop: 4, marginBottom: 16 }}
            >
              <Title
                style={{
                  fontSize: 14,
                  textTransform: 'initial',
                  width: '14%',
                  marginRight: 10,
                  flexShrink: 0,
                  minWidth: 110,
                }}
              >
                {translations?.['b2b.txt.ticketOrder']}
              </Title>
              <span
                style={{
                  fontSize: 14,
                  color: '#000000',
                  wordBreak: 'break-word',
                }}
              >
                {orderRefundData.order.id}
              </span>
            </div>
            <div
              className="d-flex align-content-center"
              style={{ marginTop: 4, marginBottom: 24 }}
            >
              <Title
                style={{
                  fontSize: 14,
                  textTransform: 'initial',
                  width: '14%',
                  marginRight: 10,
                  flexShrink: 0,
                  minWidth: 110,
                }}
              >
                {translations?.['b2b.txt.reasonCancellation']}
              </Title>
              <span
                style={{
                  fontSize: 14,
                  color: '#000000',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-line',
                }}
              >
                {orderRefundData?.order?.returnReason}
              </span>
            </div>
            {Array.isArray(giftItems) && giftItems.length > 0 ? (
              <GiftItems giftItemsData={giftItems} />
            ) : null}
            {Array.isArray(cancellationListData) &&
            cancellationListData.length > 0 ? (
              <CancellationList
                cancellationListData={orderRefundData?.tickets}
              />
            ) : null}
            <div style={{ marginTop: 28 }}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ borderBottom: 'dashed 2px #D9D9D9', padding: '8px 0' }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    textTransform: 'initial',
                    color: '#132639',
                  }}
                >
                  {translations?.['b2b.cart.totalTickets'] ?? ''}
                </Text>
                <Text style={{ fontWeight: 500 }}>
                  {formatNumber(orderRefundData?.order?.totalTicket).replaceAll(
                    ',',
                    '.',
                  )}
                </Text>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  borderBottom: 'dashed 2px #D9D9D9',
                  padding: '8px 0',
                  marginTop: 8,
                }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    textTransform: 'initial',
                    color: '#132639',
                  }}
                >
                  {translations?.['b2b.txt.totalTicketPrice'] ?? ''}
                </Text>
                <Text style={{ fontWeight: 500, color: '#E92227' }}>
                  {formatNumber(orderRefundData?.order?.totalPrice).replaceAll(
                    ',',
                    '.',
                  )}
                  đ
                </Text>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  borderBottom: 'dashed 2px #D9D9D9',
                  padding: '8px 0',
                  marginTop: 8,
                }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    textTransform: 'initial',
                    color: '#132639',
                  }}
                >
                  {translations?.['b2b.txt.totalFine'] ?? ''}
                </Text>
                <Text style={{ fontWeight: 500, color: '#E92227' }}>
                  {formatNumber(
                    orderRefundData?.order?.totalPenaltyFee,
                  ).replaceAll(',', '.')}
                  đ
                </Text>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  padding: '8px 0 0 0',
                  marginTop: 8,
                }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    textTransform: 'initial',
                    color: '#132639',
                    fontSize: 16,
                  }}
                >
                  {translations?.['b2b.txt.totalRefund'] ?? ''}
                </Text>
                <Text
                  style={{ fontWeight: 600, fontSize: 16, color: '#E92227' }}
                >
                  {formatNumber(
                    orderRefundData?.order?.totalReturnCharge,
                  ).replaceAll(',', '.')}
                  đ
                </Text>
              </div>
            </div>
          </div>
        </RefundLayout>
      ) : null}
    </LoadingContainer>
  );
};

export default TicketRefundInfo;
